import React, { useEffect, useState } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";
import DamsTable from "../../components/Table/CollapsibleTable";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { TRowItem } from "../../components/Table/TableRow";
import { usePagination } from "../../hooks/usePagination";
import { assetReportApi } from "../../services/asset-report/index";
import { Box, Stack, Button, CircularProgress } from "@mui/material";
import useAppNavigation from "../../hooks/useAppNavigation";
import appRoutePath from "../../config/path";
import { NoItemContent } from "../page-bases/NoItemContent";
import {
  IAssetReport,
  IPagination,
  IqueryParams,
  PageActions,
} from "../../config/types";
import { formatDate } from "../../utils/common";
import { usePageModels } from "../../hooks/usePageActions";
import { ModalBase } from "../../components/Modals/ModalBase";
import ActionModal from "../../components/Modals/ActionModal";
import { DamsAppAsset } from "../../assets/registry";
import { alertDialogStore, actionDialogStore } from "../../store/dialogs";
import { red } from "@mui/material/colors";
import { SimpleLabel } from "../../components/Label/SimpleLabel";
import { BadgeLabel } from "../../components/Label";

const AssetReportHistory = () => {
  const [pageState, setPageState] = useState<string>("main");
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [querySet, setQuerySet] = useState<IqueryParams>({ is_deleted: false });
  const [pageAction, updatePageAction] = useState<PageActions>();
  const [isProcessing, setProcess] = useState(false);

  const navigate = useAppNavigation();
  const { models, closeModel, openModel } = usePageModels();

  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);

  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(assetReportApi.getAssetReports);

  const populateDataSet = (data: IAssetReport[], pagination?: IPagination) => {
    const formatData = data.map((report) => ({
      ...report,
      total_assets: report.assets?.length || 0,
      asset_category: report.assets[0]?.asset_category_name || "N/A",
      serial_number: report.assets[0]?.serial_number || "N/A",
      brand: report.assets[0]?.brand || "N/A",
      batch_number: report.assets[0]?.batch_number || "N/A",
      embossment_number: report.assets[0]?.embossment_number || "N/A",
      date_reported: formatDate(report.created_at),
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  const fetchData = async (query: IqueryParams = {}) => {
    try {
      const result = await assetReportApi.getAssetReports({
        ...query,
        is_deleted: false,
      });

      if (result?.data) {
        if (result.data.length === 0) {
          setPageState("empty-db");
        } else {
          setPageState("main");
          populateDataSet(result.data, result.pagination);
        }
      }
    } catch (err) {
      setPageState("empty-db");
    }
  };

  useEffect(() => {
    fetchData();
    resetSelectedRowItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addNewItem() {
    navigate({ path: appRoutePath.ASSET_REPORT_FORM });
  }

  function clearSearch() {
    setPageState("empty-db");
  }

  function handleEdit(item: TRowItem) {
    actionDialogStore.open({
      title: "Edit Asset Report",
      subtitle: `Are you sure you want to edit this asset report?`,
      onRightBtnPress: async () =>
        navigate({
          path: appRoutePath.EDIT_ASSET_REPORT_FORM,
          params: { id: item.id },
        }),
      rightBtnTxt: "Continue",
      type: "DELETE",
    });
  }

  function goToDetailsPage(item: TRowItem) {
    navigate({
      path: appRoutePath.ASSET_REPORT_DETAIL_PAGE,
      params: {
        id: item.id,
      },
    });
  }

  function openDeleteModel(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: `Asset Report - ${item.issue}`,
    });
  }

  async function handleDeleteRequest() {
    setProcess(true);
    const res = await assetReportApi.deleteAssetReport(pageAction?.id ?? "");

    if (res.success) {
      fetchData(querySet);
    }

    setProcess(false);
    resetSelectedRowItems();
    updatePageAction({
      title: res.success ? "Delete Asset Report" : "Delete Failed",
      message:
        res.message ||
        (res.success
          ? "Asset report deleted successfully"
          : "Failed to delete asset report"),
      success: res.success,
    });
    openModel("alert");
  }

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteAction = async () => {
      setProcess(true);
      await Promise.all(
        selectedRowItems.map(async (item) => {
          await assetReportApi.deleteAssetReport(item.id);
        }),
      );
      resetSelectedRowItems();
      await fetchData(querySet);
      setProcess(false);
      alertDialogStore.open({
        title: "Batch Delete",
        subtitle: "Batch delete successful",
        isSuccess: true,
      });
      closeDialog();
    };

    const deleteActionLabel = isProcessing
      ? "( processing ...)"
      : `( ${selectedRowItems.length} selected )`;

    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <Button
          onClick={batchDeleteAction}
          variant="text"
          sx={styles.dialogItemBtn}
        >
          <SimpleLabel
            title={`Delete ${deleteActionLabel}`}
            color={red[700]}
            fontSize={13}
          />
          {isProcessing && <CircularProgress size={12} />}
        </Button>
      </Stack>
    );
  };

  const ExtraRowContent = ({ item }: { item: TRowItem }) => {
    return (
      <Stack alignItems={"center"} direction={"row"} spacing={1}>
        <BadgeLabel
          onClick={() => goToDetailsPage(item)}
          title={"Details"}
          tip="Click to view more details"
          colorType="blue"
        />
      </Stack>
    );
  };

  const page_info = getEmptyPageInfo(pageState);

  return (
    <MainPageBase
      pageState={pageState}
      title={"Asset Report History"}
      subtitle={
        "View and manage all asset issue reports and their resolutions."
      }
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText={"New Asset Report"}
    >
      <Box
        height={`100%`}
        display={"flex"}
        alignItems={pageState !== "main" ? "center" : ""}
        justifyContent={"center"}
      >
        {pageState !== "main" ? (
          <NoItemContent
            type={pageState}
            title={page_info.title}
            subtitle={page_info.message}
            addItemBtn={() => addNewItem()}
            clearSearchBtn={() => clearSearch()}
          />
        ) : (
          <Stack width={"100%"}>
            <DamsTable
              tableTitle="Report History"
              tableSubtitle="Track issues, resolutions and evidence for all reported assets."
              useTableCheckbox={true}
              selectedRowItems={selectedRowItems}
              tableHeaders={columns}
              tableKeys={tkeys}
              hiddenKeys={hkeys}
              hiddenLabels={hcolumns}
              data={dataset}
              maxRowCells={7}
              onTableItemSelect={toggleRow}
              toggleSelectAllRow={toggleSelectAllRow}
              onTableItemEdit={handleEdit}
              onTableItemDelete={openDeleteModel}
              hasPagination
              paginationState={pagination}
              onNextPage={paginateToNextPage(querySet, populateDataSet)}
              onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
              setActivePage={paginateToPageNumber(populateDataSet, querySet)}
              headerDropDownMenu={renderHeaderDropDownMenu}
              tableTitleBadge={
                selectedRowItems.length === 0
                  ? undefined
                  : selectedRowItems.length + " selected"
              }
              extraRowComponent={ExtraRowContent}
            />
          </Stack>
        )}
      </Box>

      <ModalBase
        open={models.showDeleteModel}
        title={"Delete Asset Report"}
        subtitle={
          <Box>
            Once you delete{" "}
            <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>, ONLY
            admin can restore. Are you sure you want to proceed?
          </Box>
        }
        type={"action"}
        showHeader={true}
        leftIcon={DamsAppAsset.icons.delete_icon}
        leftBtnTxt="Cancel"
        onLeftBtnPress={closeModel}
        rightBtnTxt="Delete"
        onRightBtnPress={handleDeleteRequest}
        onRightBtnLoading={isProcessing}
        onClose={closeModel}
      />

      <ActionModal
        show={models.showAlertModel}
        title={pageAction?.title ?? ""}
        leftIcon={
          pageAction?.success === false ? DamsAppAsset.icons.warning : undefined
        }
        subtitle={pageAction?.message ?? ""}
        onLeftBtnPress={closeModel}
        leftBtnTxt={"close"}
        onRightBtnPress={closeModel}
        rightBtnTxt={"continue"}
        onClosePress={closeModel}
      />
    </MainPageBase>
  );
};

export default AssetReportHistory;

const tkeys = ["batch_number", "total_assets", "issue", "date_reported"];

const columns = ["Batch Number", "Total Assets", "Issue", "Date Reported"];

const hkeys = ["comments", "resolution"];

const hcolumns = ["Comment", "Resolution"];

const styles = {
  dialogItemBtn: {
    textTransform: "none",
    width: "100%",
    padding: 1,
    alignText: "left",
  },
};

const getEmptyPageInfo = (key: string): { title: string; message: string } => {
  if (page_msg.hasOwnProperty(key)) {
    return page_msg[key];
  } else {
    return { title: "", message: "" };
  }
};

const page_msg: { [key: string]: { title: string; message: string } } = {
  "empty-db": {
    title: "No Asset Reports Found",
    message:
      'No asset reports have been added yet. Click the "New Asset Report" button to report an asset issue.',
  },
  "empty-search": {
    title: "No Asset Reports Found",
    message:
      "No asset reports found matching your search criteria. Please try different search terms or add a new asset report.",
  },
};
